
import { defineComponent, ref, onMounted, computed } from "vue";
import { RevenueDashboardList, RevenueDashboardState, RevenueOverviewApiRequest, RevenueBreakdownApiRequest, RevenueDashboardBreakdownData } from "@/domain/entities/RevenueDashboard";
import FilterGrouping from "../components/filter-grouping.vue";
import CardTotalProfit from "../components/card-total-profit.vue";
import TotalRevenueSection from "./components/total-revenue-section.vue";
import { RevenueDashboardController } from "@/app/ui/controllers/RevenueDashboardController";
import { formatDateNormal } from "@/app/infrastructures/misc/Utils";
import BreakdownTable from "./components/breakdown-table.vue";
import RevenueMetricFilter from "@/app/ui/views/dashboard/revenue/components/filters/revenue-metric-filter.vue";
import RevenueBreakdownFilter from "@/app/ui/views/dashboard/revenue/components/filters/revenue-breakdown-filter.vue";
import TopCommoditySection from "@/app/ui/views/dashboard/revenue/components/top-commodity-section.vue";
import LineComparisonChart from "./components/line-comparison-chart.vue";
import { FcsPermissionRevenueDashboard } from "@/feature-flags/flags-permission-revenue-dashboard";

export default defineComponent({
  name: "TabRevenue",
  components: {
    FilterGrouping,
    CardTotalProfit,
    TotalRevenueSection,
    BreakdownTable,
    RevenueMetricFilter,
    RevenueBreakdownFilter,
    TopCommoditySection,
    LineComparisonChart
  },
  setup() {
    const revenueData = ref(new RevenueDashboardList());
    const stateData = ref(new RevenueDashboardState());

    const getRevenueOverview = async () => {
      try {
        stateData.value.isLoadingOverview = true;
        stateData.value.errorOverview = false;
        const resp = await RevenueDashboardController.getRevenueOverview(
          new RevenueOverviewApiRequest({
            startDate: formatDateNormal(
              revenueData.value.params.dates.main.start,
              "YYYY-MM-DD"
            ),
            endDate: formatDateNormal(
              revenueData.value.params.dates.main.end,
              "YYYY-MM-DD"
            ),
            comparisonStartDate: formatDateNormal(
              revenueData.value.params.dates.compare.start,
              "YYYY-MM-DD"
            ),
            comparisonEndDate: formatDateNormal(
              revenueData.value.params.dates.compare.end,
              "YYYY-MM-DD"
            )
          } as any)
        );
        stateData.value.overviewData = resp as any;
      } catch (error) {
        console.log("🚀 ~ getRevenueOverview ~ error:", error);
        stateData.value.errorOverview = true;
      } finally {
        stateData.value.isLoadingOverview = false;
      }
    };

    const getBreakdown = async () => {
      try {
        stateData.value.isLoadingBreakdown = true;
        stateData.value.isErrorBreakdown = false;
        const resp = await RevenueDashboardController.fetchRevenueBreakdown(
          new RevenueBreakdownApiRequest({
            dataType:
              stateData.value.dataGrouping === "omzet"
                ? "revenue"
                : stateData.value.dataGrouping === "berat"
                ? "weight"
                : stateData.value.dataGrouping,
            startDate: formatDateNormal(
              revenueData.value.params.dates.main.start,
              "YYYY-MM-DD"
            ),
            endDate: formatDateNormal(
              revenueData.value.params.dates.main.end,
              "YYYY-MM-DD"
            ),
            comparisonStartDate: formatDateNormal(
              revenueData.value.params.dates.compare.start,
              "YYYY-MM-DD"
            ),
            comparisonEndDate: formatDateNormal(
              revenueData.value.params.dates.compare.end,
              "YYYY-MM-DD"
            ),
            groupBy: revenueData.value.params.groupBy.value || "daily",
            codTypes: stateData.value.codTypes,
            channels: stateData.value.channels,
            productTypes: stateData.value.productTypes,
            destination3lc: stateData.value.destination3lc,
            commodityCodes: stateData.value.commodityCodes
          } as any)
        );
        stateData.value.breakdownData = resp as any;
      } catch (error) {
        stateData.value.isErrorBreakdown = true;
        stateData.value.errorBreakdown = "";
      } finally {
        stateData.value.isLoadingBreakdown = false;
      }
    };

    const isShowTopCommodity = computed(() => {
      return FcsPermissionRevenueDashboard.permission_revenue_top_commodity_enable.isEnabled();
    });

    const getData = async () => {
      getRevenueOverview();
      getBreakdown();
    };

    return {
      revenueData,
      getData,
      getRevenueOverview,
      stateData,
      getBreakdown,
      isShowTopCommodity
    };
  }
});
